import React from "react";

import DynamicTableVirtual from "../components/table-virtual";
import Topbar from "../components/topbar";
import { useListSearch, ListTitle } from "../components/ui/list-search";
import AddContributionModal from "../components/popup/add-contribution";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import { useMediaQuery, useTheme, Button, Box, Fab } from "@mui/material";
import { Menu, Plus } from "lucide-react";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";
import type { Column } from '../components/table-virtual';
import AddBtn from "../components/ui/add-btn";
import contributionImage from '../files/images/contribution.png';

import routes from "../api/routes";
import useApi from "../hooks/use-api";
import buildApiRequestConfig from "../api/url-builder";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useToast } from "../providers/toaster-provider";
import { DropdownOption } from "../components/ui/dropdown";

export type Contribution = {
    id: string;
    name: string;
    category: string;
    enabled: boolean;
    orderNumber?: number;
};

const Contributions: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const canCreate = can('staff_payment', 'create');
    const canUpdate = can('staff_payment', 'update');
    const canDelete = can('staff_payment', 'delete');
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const [data, setData] = React.useState<Contribution[]>([]);
    const list = useListSearch(data);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedContributionId, setSelectedContributionId] = React.useState<string | null>(null);
    const [categoryOptions, setCategoryOptions] = React.useState<DropdownOption[]>([]);

    const [fetch, setFetch] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [create, setCreate] = useApi();
    const [toggle, setToggle] = useApi();
    const [reorder, setReorder] = useApi();
    const [fetchCategories, setFetchCategories] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Staff Payment";
    }, []);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.contributions.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetch({ url, method });
    }, [user, update, remove, create]);

    // After the contributions load - that fetch may have just had the AI
    // create a category for one of them (staff-contribution-service.js).
    React.useEffect(() => {
        if (fetch.isLoading || !fetch.data) return;
        const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchCategories({ url, method });
    }, [user, fetch.isLoading, fetch.data]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const { data, isLoading } = fetchCategories;
        if (!isLoading && data) {
            // Subcategories too - a contribution can sit under one.
            const flatten = (cats: any[]): DropdownOption[] => cats.flatMap((cat: any) => [{ label: cat.name, value: cat.id }, ...flatten(cat.children || [])]);
            setCategoryOptions(flatten(Array.isArray(data) ? data : []));
        }
    }, [fetchCategories]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (!isLoading && data) {
            setData(data.map((contribution: any) => ({
                id: contribution.id,
                name: contribution.name,
                category: categoryOptions.find(option => option.value === contribution.categoryId)?.label || null,
                enabled: contribution.isEnabled,
                orderNumber: contribution.orderNumber,
            })));
        }
        if (error) {
            showError(error ? error.toString() : "Failed to fetch contributions.");
        }
    }, [fetch, categoryOptions]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (!isLoading && data) {
            showSuccess("Contribution created successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create contributions.");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (!isLoading && data) {
            showSuccess("Contribution updated successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update contributions.");
        }
    }, [update]);

    React.useEffect(() => {
        const { error } = reorder;
        if (error) {
            showError(error ? error.toString() : "Failed to reorder contributions.");
        }
    }, [reorder]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (!isLoading && data) {
            showSuccess("Contribution deleted successfully.");
            setOpenModal('');
        }
        if (error) {
            showError(error ? error.toString() : "Failed to delete contributions.");
        }
    }, [remove]);

    const handleCreateContribution = (contribution: { name: string; category: string }) => {
        const isDuplicate = data.some(c => c.name.trim().toLowerCase() === contribution.name.trim().toLowerCase());
        if (isDuplicate) {
            showError('A contribution with this name already exists.');
            return;
        }
        const { url, method } = buildApiRequestConfig(routes.contributions.create);
        setCreate({ url, method, body: { ...contribution, entityId: user?.entityId, isEnabled: true, categoryId: contribution.category } });
    };

    const handleUpdateContribution = (contribution: { name: string; category: string }) => {
        if (!selectedContributionId) return;

        const isDuplicate = data.some(c => c.name.trim().toLowerCase() === contribution.name.trim().toLowerCase() && String(c.id) !== String(selectedContributionId));
        if (isDuplicate) {
            showError('A contribution with this name already exists.');
            return;
        }
        const { url, method } = buildApiRequestConfig(routes.contributions.update, [{ param: 'id', value: selectedContributionId }]);
        setUpdate({ url, method, body: { ...contribution, entityId: user?.entityId, categoryId: contribution.category } });
    }

    const handleDeleteContribution = () => {
        if (!selectedContributionId) return;

        const { url, method } = buildApiRequestConfig(routes.contributions.delete, [{ param: 'id', value: selectedContributionId }]);
        setRemove({ url, method });
    }

    const handleToggle = (id: string, enabled: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.contributions.toggleStatus, [{ param: 'id', value: id }]);
        setToggle({ url, method, body: { isEnabled: !enabled } });
    }

    const handleReorder = (reorderedData: Record<string, any>[]) => {
        // Find which item moved to a new position
        for (let i = 0; i < reorderedData.length; i++) {
            if (data[i]?.id !== reorderedData[i]?.id) {
                // This item moved - send ONE request with its new orderNumber
                const movedItem = reorderedData[i];
                if (movedItem.id) {
                    const { url, method } = buildApiRequestConfig(routes.contributions.update, [{ param: 'id', value: movedItem.id }]);
                    setReorder({ url, method, body: { orderNumber: i + 1 } });
                }
                return;
            }
        }
    }

    const columns: Column[] = [
        { id: 'name', label: 'Contribution Name', size: 150 },
        { id: 'category', label: 'Category', size: 150 },
        { id: 'enabled', label: 'Enabled', size: 150, type: 'toggle', onToggle: (row) => handleToggle(row.id, row.enabled) },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 150, 
            type: 'actions', 
            onDelete: (row) => {setOpenModal('delete'); setSelectedContributionId(row.id);}, 
            onEdit: (row) => {setOpenModal('edit'); setSelectedContributionId(row.id);}
        },
    ];
    
    return (
        <div style={{ width: '100%' }}>
            <AddContributionModal
                isEdit={{ id: openModal === 'edit' ? Number(selectedContributionId) : null, state: openModal === 'edit' }}
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                categoryOptions={categoryOptions}
                onConfirm={openModal === 'add' ? handleCreateContribution : handleUpdateContribution}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Contribution?"
                description="Are you sure you want to delete this contribution? Existing staff configurations using this contribution may be affected."
                onCancel={() => setOpenModal('')}
                image={{ src: contributionImage, width: 150, height: 150 }}
                onConfirm={handleDeleteContribution}
            />
            <Topbar
                title={<ListTitle title="Staff Payment" list={list} />}
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                    {!isMobile && (
                        <AddBtn disabled={!canCreate} onClick={() => setOpenModal('add')} label="Add Contribution" />
                    )}
                </Box>
            </Topbar>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={list.filtered}
                    isLoading={fetch.isLoading}
                    scrollMode="page"
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                    onReorder={handleReorder}
                    enableRowDragAndDrop={true}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && canCreate && (
                <Fab
                    onClick={() => setOpenModal('add')}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        background: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': { background: 'var(--color-primary-hover)' },
                        zIndex: 999,
                    }}
                >
                    <Plus size={24} />
                </Fab>
            )}
        </div>
    );
};

export default Contributions;